import React from 'react';
import { Link } from 'react-router-dom';
import { WebLayout } from '../components/WebLayout';
import { ProfileHeader } from '../components/ProfileHeader';
import { AchievementBadge } from '../components/AchievementBadge';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Flame, Target, Zap, Trophy, Clock, TrendingUp, Settings } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
const allAchievements = [{
  id: 'first_quest',
  icon: '🎯',
  title: 'First Quest',
  description: 'Complete your first quest',
  requirement: 1
}, {
  id: 'quest_master',
  icon: '⭐',
  title: 'Quest Master',
  description: 'Complete 10 quests',
  requirement: 10
}, {
  id: 'half_century',
  icon: '🏅',
  title: 'Half Century',
  description: 'Complete 50 quests',
  requirement: 50
}, {
  id: 'century',
  icon: '💯',
  title: 'Century',
  description: 'Complete 100 quests',
  requirement: 100
}, {
  id: 'focus_beginner',
  icon: '🧘',
  title: 'Focus Beginner',
  description: 'Complete 5 focus sessions',
  requirement: 5
}, {
  id: 'focus_master',
  icon: '🎯',
  title: 'Focus Master',
  description: 'Complete 50 focus sessions',
  requirement: 50
}, {
  id: 'time_lord',
  icon: '⏰',
  title: 'Time Lord',
  description: 'Focus for 1000 minutes total',
  requirement: 1000
}, {
  id: 'early_bird',
  icon: '🌅',
  title: 'Early Bird',
  description: 'Complete 5 tasks before 9AM',
  requirement: 5
}, {
  id: 'night_owl',
  icon: '🦉',
  title: 'Night Owl',
  description: 'Complete 5 tasks after 10PM',
  requirement: 5
}, {
  id: 'streak_warrior',
  icon: '🔥',
  title: 'Streak Warrior',
  description: 'Maintain a 7-day streak',
  requirement: 7
}, {
  id: 'streak_legend',
  icon: '👑',
  title: 'Streak Legend',
  description: 'Maintain a 30-day streak',
  requirement: 30
}, {
  id: 'level_5',
  icon: '⚡',
  title: 'Level 5',
  description: 'Reach Level 5',
  requirement: 5
}, {
  id: 'level_10',
  icon: '💫',
  title: 'Level 10',
  description: 'Reach Level 10',
  requirement: 10
}, {
  id: 'level_25',
  icon: '🌟',
  title: 'Level 25',
  description: 'Reach Level 25',
  requirement: 25
}, {
  id: 'perfectionist',
  icon: '✨',
  title: 'Perfectionist',
  description: 'Complete 10 high-priority quests',
  requirement: 10
}, {
  id: 'multitasker',
  icon: '🎪',
  title: 'Multitasker',
  description: 'Have 5 active quests at once',
  requirement: 5
}];
export function ProfilePage() {
  const {
    stats
  } = useUser();
  const statCards = [{
    label: 'Streak',
    value: `${stats.streak} Days`,
    icon: Flame,
    color: 'text-orange-500 bg-orange-100 dark:bg-orange-900/30'
  }, {
    label: 'Completed',
    value: stats.completedGoals,
    icon: Target,
    color: 'text-blue-500 bg-blue-100 dark:bg-blue-900/30'
  }, {
    label: 'Focus',
    value: `${stats.focusSessions}`,
    icon: Zap,
    color: 'text-purple-500 bg-purple-100 dark:bg-purple-900/30'
  }, {
    label: 'Time',
    value: `${Math.floor(stats.totalFocusMinutes / 60)}h`,
    icon: Clock,
    color: 'text-green-500 bg-green-100 dark:bg-green-900/30'
  }, {
    label: 'Level',
    value: stats.level,
    icon: TrendingUp,
    color: 'text-pink-500 bg-pink-100 dark:bg-pink-900/30'
  }, {
    label: 'XP',
    value: stats.xp,
    icon: Trophy,
    color: 'text-yellow-500 bg-yellow-100 dark:bg-yellow-900/30'
  }];
  return <WebLayout data-id="element-1122">
      <div className="lg:bg-white lg:dark:bg-[#252936]" data-id="element-1123">
        <div className="bg-white dark:bg-[#252936] pb-8 pt-6 lg:pt-8 rounded-b-[3rem] lg:rounded-none shadow-sm lg:shadow-none mb-6 lg:border-b lg:border-gray-200 lg:dark:border-gray-800" data-id="element-1124">
          <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1125">
            <ProfileHeader data-id="element-1126" />

            <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 lg:gap-4 mt-8" data-id="element-1127">
              {statCards.map(stat => <div key={stat.label} className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-3 lg:p-4 text-center" data-id="element-1128">
                  <div className={`w-10 h-10 lg:w-12 lg:h-12 mx-auto rounded-full flex items-center justify-center mb-2 ${stat.color}`} data-id="element-1129">
                    <stat.icon className="w-5 h-5 lg:w-6 lg:h-6" data-id="element-1130" />
                  </div>
                  <div className="font-fredoka font-bold text-game-dark dark:text-white text-lg lg:text-xl" data-id="element-1131">
                    {stat.value}
                  </div>
                  <div className="text-xs text-gray-500 font-quicksand font-bold uppercase" data-id="element-1132">
                    {stat.label}
                  </div>
                </div>)}
            </div>
          </div>
        </div>

        <main className="px-6 lg:px-8 space-y-6 lg:space-y-8 max-w-7xl mx-auto" data-id="element-1133">
          <section data-id="element-1134">
            <div className="flex justify-between items-center mb-4 lg:mb-6" data-id="element-1135">
              <h3 className="font-fredoka font-bold text-xl lg:text-2xl text-game-dark dark:text-white" data-id="element-1136">
                Achievements
              </h3>
              <span className="text-sm font-quicksand font-bold text-gray-500" data-id="element-1137">
                {stats.achievements.length}/{allAchievements.length}
              </span>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6" data-id="element-1138">
              {allAchievements.map(achievement => <AchievementBadge key={achievement.id} {...achievement} unlocked={stats.achievements.includes(achievement.id)} data-id="element-1139" />)}
            </div>
          </section>

          <Card className="p-6" data-id="element-1140">
            <h3 className="font-fredoka font-bold text-game-dark dark:text-white mb-4" data-id="element-1141">
              Progress to Next Level
            </h3>
            <div className="space-y-2" data-id="element-1142">
              <div className="flex justify-between text-sm font-quicksand" data-id="element-1143">
                <span className="text-gray-600 dark:text-gray-400" data-id="element-1144">
                  Level {stats.level}
                </span>
                <span className="text-gray-600 dark:text-gray-400" data-id="element-1145">
                  Level {stats.level + 1}
                </span>
              </div>
              <div className="h-4 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden" data-id="element-1146">
                <div className="h-full bg-gradient-to-r from-game-purple to-game-teal transition-all duration-500" style={{
                width: `${stats.xp % 1000 / 1000 * 100}%`
              }} data-id="element-1147" />
              </div>
              <p className="text-center text-sm font-quicksand text-gray-600 dark:text-gray-400" data-id="element-1148">
                {stats.xp % 1000} / 1000 XP
              </p>
            </div>
          </Card>

          <Link to="/settings" className="block lg:hidden" data-id="element-1149">
            <Button variant="secondary" className="w-full" icon={<Settings className="w-5 h-5" data-id="element-1151" />} data-id="element-1150">
              Settings
            </Button>
          </Link>
        </main>
      </div>
    </WebLayout>;
}